import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'

export type TimelineMaintenanceAction = 'rename' | 'move' | 'archive' | 'restore' | 'delete'

export interface TimelineMaintenanceOperationChoice {
  readonly id: string
  readonly name: string
  readonly active?: boolean
  readonly restoreSelectedByDefault?: boolean
}

export interface TimelineMaintenanceCategoryChoice {
  readonly id: string
  readonly name: string
}

export interface TimelineMaintenanceTarget {
  readonly kind: 'category' | 'operation'
  readonly id: string
  readonly name: string
  readonly active: boolean
  readonly categoryId?: string
  readonly operations?: readonly TimelineMaintenanceOperationChoice[]
}

export type TimelineMaintenanceRequest =
  | { readonly action: 'rename'; readonly target: TimelineMaintenanceTarget; readonly name: string }
  | { readonly action: 'move'; readonly target: TimelineMaintenanceTarget; readonly categoryId: string }
  | { readonly action: 'archive'; readonly target: TimelineMaintenanceTarget; readonly effectiveFrom: string; readonly operationIds?: readonly string[] }
  | { readonly action: 'restore'; readonly target: TimelineMaintenanceTarget; readonly effectiveFrom: string; readonly operationIds?: readonly string[] }
  | { readonly action: 'delete'; readonly target: TimelineMaintenanceTarget }

export interface TimelineMaintenanceBackup {
  readonly filename: string
  readonly content: string
  readonly sha256: string
}

export interface TimelineMaintenanceConfirmation {
  readonly typedName?: string
  readonly backupChecksum?: string
  readonly backupDownloadInitiated: boolean
}

export interface TimelineMaintenanceImpactRow {
  readonly label: string
  readonly value: string
}

export interface TimelineMaintenancePreview {
  readonly request: TimelineMaintenanceRequest
  readonly impact: readonly TimelineMaintenanceImpactRow[]
  readonly affectedOperations?: readonly { readonly id: string; readonly name: string }[]
  readonly warnings?: readonly string[]
  readonly backup?: TimelineMaintenanceBackup
}

export interface TimelineMaintenanceControlsProps {
  readonly target: TimelineMaintenanceTarget
  readonly today: string
  readonly categories: readonly TimelineMaintenanceCategoryChoice[]
  readonly disabled?: boolean
  readonly onPreview: (request: TimelineMaintenanceRequest) => Promise<TimelineMaintenancePreview>
  readonly onApply: (preview: TimelineMaintenancePreview, confirmation: TimelineMaintenanceConfirmation) => Promise<void>
  readonly onDownloadBackup: (backup: TimelineMaintenanceBackup) => Promise<void> | void
  readonly onComplete?: (preview: TimelineMaintenancePreview) => void
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

function actionLabel(action: TimelineMaintenanceAction): string {
  if (action === 'delete') return 'Delete permanently'
  return `${action[0]!.toUpperCase()}${action.slice(1)}`
}

function dialogTitle(action: TimelineMaintenanceAction, name: string): string {
  if (action === 'archive') return `Archive ${name}`
  if (action === 'restore') return `Restore ${name}`
  if (action === 'delete') return `Delete ${name} permanently`
  return `${actionLabel(action)} ${name}`
}

function trapFocus(event: KeyboardEvent<HTMLElement>, container: HTMLElement) {
  if (event.key !== 'Tab') return
  const focusable = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)]
  if (focusable.length === 0) return
  const first = focusable[0]!
  const last = focusable.at(-1)!
  if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus() }
}

export function TimelineMaintenanceControls({
  target,
  today,
  categories,
  disabled = false,
  onPreview,
  onApply,
  onDownloadBackup,
  onComplete,
}: TimelineMaintenanceControlsProps) {
  const id = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const dialogFocusTimer = useRef<number | null>(null)
  const generation = useRef(0)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const [action, setAction] = useState<TimelineMaintenanceAction | null>(null)
  const [name, setName] = useState(target.name)
  const [categoryId, setCategoryId] = useState(categories.find((category) => category.id !== target.categoryId)?.id ?? '')
  const [effectiveFrom, setEffectiveFrom] = useState(today)
  const [selectedOperationIds, setSelectedOperationIds] = useState<readonly string[] | null>([])
  const [preview, setPreview] = useState<TimelineMaintenancePreview | null>(null)
  const [confirmation, setConfirmation] = useState('')
  const [backupDownloaded, setBackupDownloaded] = useState(false)
  const [busy, setBusy] = useState<'preview' | 'backup' | 'apply' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const availableActions: readonly TimelineMaintenanceAction[] = target.kind === 'operation'
    ? ['rename', 'move', target.active ? 'archive' : 'restore', 'delete']
    : ['rename', target.active ? 'archive' : 'restore', 'delete']

  const resetDraft = (next: TimelineMaintenanceAction) => {
    generation.current += 1
    setName(target.name)
    setCategoryId(categories.find((category) => category.id !== target.categoryId)?.id ?? '')
    setEffectiveFrom(today)
    setSelectedOperationIds(next === 'archive'
      ? target.kind === 'category' ? null : []
      : next === 'restore'
      ? target.operations?.some((operation) => operation.restoreSelectedByDefault !== undefined)
        ? target.operations.filter((operation) => operation.restoreSelectedByDefault === true).map((operation) => operation.id)
        : null
      : [])
    setPreview(null)
    setConfirmation('')
    setBackupDownloaded(false)
    setError(null)
  }

  const invalidatePreview = () => {
    if (busy) return
    generation.current += 1
    setPreview(null)
    setConfirmation('')
    setBackupDownloaded(false)
    setError(null)
  }

  const closeMenu = (restoreFocus = true) => {
    setMenuOpen(false)
    if (restoreFocus) window.setTimeout(() => triggerRef.current?.focus(), 0)
  }

  const openMenu = (focusFirst = false) => {
    const bounds = triggerRef.current?.getBoundingClientRect()
    if (bounds) setMenuPosition({
      top: Math.min(bounds.bottom + 5, window.innerHeight - 190),
      left: Math.max(8, Math.min(bounds.right - 200, window.innerWidth - 208)),
    })
    setMenuOpen(true)
    if (focusFirst) window.setTimeout(() => menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus(), 0)
  }

  const closeDialog = () => {
    if (busy) return
    generation.current += 1
    setAction(null)
    setPreview(null)
    setError(null)
    window.setTimeout(() => triggerRef.current?.focus(), 0)
  }

  const startAction = (next: TimelineMaintenanceAction) => {
    setMenuOpen(false)
    resetDraft(next)
    setAction(next)
  }

  useEffect(() => {
    if (!action) return
    dialogFocusTimer.current = window.setTimeout(() => {
      dialogFocusTimer.current = null
      dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    }, 0)
    return () => {
      if (dialogFocusTimer.current !== null) window.clearTimeout(dialogFocusTimer.current)
      dialogFocusTimer.current = null
    }
  }, [action])

  useEffect(() => {
    generation.current += 1
    setMenuOpen(false)
    setAction(null)
    setPreview(null)
    setError(null)
  }, [target.id, target.kind])

  useEffect(() => () => { generation.current += 1 }, [])

  const requestForAction = (): TimelineMaintenanceRequest => {
    if (action === 'rename') return { action, target, name: name.trim() }
    if (action === 'move') return { action, target, categoryId }
    if (action === 'archive') return { action, target, effectiveFrom, ...(selectedOperationIds === null ? {} : { operationIds: selectedOperationIds }) }
    if (action === 'restore') return { action, target, effectiveFrom, ...(selectedOperationIds === null ? {} : { operationIds: selectedOperationIds }) }
    if (action === 'delete') return { action, target }
    throw new Error('Choose a maintenance action.')
  }

  const submitPreview = (event: FormEvent) => {
    event.preventDefault()
    let request: TimelineMaintenanceRequest
    try {
      request = requestForAction()
      if (request.action === 'rename' && !request.name) throw new Error('Enter a name before previewing.')
      if (request.action === 'move' && !request.categoryId) throw new Error('Choose a destination category before previewing.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The maintenance request is incomplete.')
      return
    }
    const requestGeneration = ++generation.current
    setBusy('preview')
    setError(null)
    void onPreview(request).then((result) => {
      if (generation.current !== requestGeneration) return
      if (result.request.action === 'restore' && result.request.operationIds) setSelectedOperationIds(result.request.operationIds)
      if (result.request.action === 'archive' && result.request.target.kind === 'category') setSelectedOperationIds(result.affectedOperations?.map((operation) => operation.id) ?? [])
      setPreview(result)
    }).catch((caught) => {
      if (generation.current !== requestGeneration) return
      setPreview(null)
      setError(caught instanceof Error ? caught.message : 'The exact impact preview failed.')
    }).finally(() => {
      if (generation.current === requestGeneration) setBusy(null)
    })
  }

  const downloadBackup = async () => {
    if (!preview?.backup || busy) return
    setBusy('backup')
    setError(null)
    try {
      await onDownloadBackup(preview.backup)
      setBackupDownloaded(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The required backup could not be downloaded.')
    } finally {
      setBusy(null)
    }
  }

  const apply = async () => {
    if (!preview || busy) return
    setBusy('apply')
    setError(null)
    try {
      await onApply(preview, {
        ...(action === 'delete' ? { typedName: confirmation, backupChecksum: preview.backup?.sha256 } : {}),
        backupDownloadInitiated: backupDownloaded,
      })
      onComplete?.(preview)
      setAction(null)
      setPreview(null)
      window.setTimeout(() => triggerRef.current?.focus(), 0)
    } catch (caught) {
      if (caught && typeof caught === 'object' && 'status' in caught && caught.status === 409) {
        setPreview(null)
        setConfirmation('')
        setBackupDownloaded(false)
      }
      setError(caught instanceof Error ? caught.message : 'The maintenance action was not applied.')
    } finally {
      setBusy(null)
    }
  }

  const dialogKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (dialogFocusTimer.current !== null) {
      window.clearTimeout(dialogFocusTimer.current)
      dialogFocusTimer.current = null
    }
    if (event.key === 'Escape') {
      if (!busy) { event.preventDefault(); closeDialog() }
      return
    }
    trapFocus(event, event.currentTarget)
  }

  const menuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')]
    const index = items.indexOf(document.activeElement as HTMLButtonElement)
    if (event.key === 'Escape') { event.preventDefault(); closeMenu() }
    else if (event.key === 'ArrowDown') { event.preventDefault(); items[(index + 1) % items.length]?.focus() }
    else if (event.key === 'ArrowUp') { event.preventDefault(); items[(index - 1 + items.length) % items.length]?.focus() }
    else if (event.key === 'Home') { event.preventDefault(); items[0]?.focus() }
    else if (event.key === 'End') { event.preventDefault(); items.at(-1)?.focus() }
  }

  return <div className="timeline-maintenance-controls">
    <button
      ref={triggerRef}
      type="button"
      className="button compact-button"
      disabled={disabled || busy !== null}
      aria-label={`Actions for ${target.name}`}
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      aria-controls={`${id}-menu`}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          openMenu(true)
        }
      }}
      onClick={() => menuOpen ? closeMenu(false) : openMenu()}
    >Actions</button>
    {menuOpen && createPortal(<div ref={menuRef} id={`${id}-menu`} className="timeline-maintenance-menu" style={menuPosition} role="menu" aria-label={`Actions for ${target.name}`} onKeyDown={menuKeyDown}>
      {availableActions.map((item) => <button type="button" role="menuitem" key={item} onClick={() => startAction(item)}>{actionLabel(item)}</button>)}
    </div>, document.body)}

    {action && createPortal(<div className="dialog-backdrop">
      <section ref={dialogRef} className="workflow-dialog timeline-maintenance-dialog panel" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} tabIndex={-1} onKeyDownCapture={dialogKeyDown}>
        <header><span className="overline">Timeline maintenance</span><h2 id={`${id}-title`}>{dialogTitle(action, target.name)}</h2></header>
        <form onSubmit={submitPreview}>
          <fieldset disabled={busy !== null}>
            <legend className="sr-only">{actionLabel(action)} settings</legend>
            {action === 'rename' && <label>New name<input autoFocus required value={name} onChange={(event) => { invalidatePreview(); setName(event.target.value) }} /></label>}
            {action === 'move' && <label>Move to category<select autoFocus required value={categoryId} onChange={(event) => { invalidatePreview(); setCategoryId(event.target.value) }}><option value="">Choose category</option>{categories.filter((category) => category.id !== target.categoryId).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>}
            {action === 'archive' && <label>Archive starting [date]<input autoFocus aria-label="Archive starting date" type="date" required value={effectiveFrom} onChange={(event) => { invalidatePreview(); if (target.kind === 'category') setSelectedOperationIds(null); setEffectiveFrom(event.target.value) }} /></label>}
            {action === 'restore' && <label>Restore starting [date]<input autoFocus aria-label="Restore starting date" type="date" required value={effectiveFrom} onChange={(event) => { invalidatePreview(); if (target.kind === 'category') setSelectedOperationIds(null); setEffectiveFrom(event.target.value) }} /></label>}
            {target.kind === 'category' && (action === 'archive' || action === 'restore') && <fieldset>
              <legend>{action === 'archive' ? 'Operations archived with this category' : 'Operations to restore'}</legend>
              {action === 'archive' && selectedOperationIds === null && <p>Preview exact impact to load operations active on the selected date.</p>}
              {action === 'restore' && selectedOperationIds === null && <p>Preview exact impact to load operations archived by the category cascade.</p>}
              {selectedOperationIds !== null && (target.operations ?? []).map((operation) => {
                const checked = selectedOperationIds.includes(operation.id)
                return <label key={operation.id}><input type="checkbox" checked={checked} disabled={action === 'archive'} onChange={(event) => { invalidatePreview(); setSelectedOperationIds((current) => {
                  const selected = current ?? []
                  return event.target.checked ? [...selected, operation.id] : selected.filter((id) => id !== operation.id)
                }) }} />{operation.name}</label>
              })}
            </fieldset>}
            {action === 'delete' && <p>Permanent Delete removes this item and its affected history after required safeguards.</p>}
            {!preview && <button type="submit" className="button button-primary">Preview exact impact</button>}
          </fieldset>
        </form>

        {error && <p role="alert">{error}</p>}
        {preview && <section aria-label="Exact maintenance impact" aria-live="polite">
          <h3>Exact impact preview</h3>
          <dl>{preview.impact.map((row, index) => <div key={`${row.label}:${index}`}><dt>{row.label}</dt><dd>{row.value}</dd></div>)}</dl>
          {preview.affectedOperations && <div><strong>Affected operations</strong><ul>{preview.affectedOperations.map((operation) => <li key={operation.id}>{operation.name}</li>)}</ul></div>}
          {preview.warnings?.map((warning) => <p className="callout" key={warning}>{warning}</p>)}
          {action === 'delete' && <div className="maintenance-delete-safeguards">
            <button type="button" className="button" disabled={!preview.backup || busy !== null} onClick={() => void downloadBackup()}>{busy === 'backup' ? 'Downloading…' : backupDownloaded ? 'Backup downloaded' : 'Download required backup'}</button>
            <label>Type <strong>{target.name}</strong> to confirm<input value={confirmation} disabled={!backupDownloaded || busy !== null} onChange={(event) => setConfirmation(event.target.value)} /></label>
          </div>}
        </section>}
        <footer>
          <button type="button" className="button" disabled={busy !== null} onClick={closeDialog}>Cancel</button>
          {preview && <button type="button" className={action === 'delete' ? 'button button-danger' : 'button button-primary'} disabled={busy !== null || action === 'delete' && (!backupDownloaded || confirmation !== target.name)} onClick={() => void apply()}>{busy === 'apply' ? 'Applying…' : action === 'delete' ? 'Delete permanently' : `Confirm ${actionLabel(action).toLowerCase()}`}</button>}
        </footer>
      </section>
    </div>, document.body)}
  </div>
}
